import { useCallback, useRef, useState } from 'react';
import { CalendarFragmentKey, LayoutZone } from '../model';

export interface DropTarget {
  zone: LayoutZone;
  index: number;
}

/**
 * Native HTML5 drag-and-drop state for the layout designer.
 *
 * Native rather than a library: cross-zone dragging is the only requirement, and adding a
 * drag-and-drop dependency for one admin screen is not worth the supply-chain surface.
 *
 * The dragged key is mirrored into a ref because `dataTransfer.getData()` is deliberately
 * unreadable during `dragover` — only on `drop` — and the drop indicator has to follow the pointer
 * before then.
 */
export const useLayoutDnd = () => {
  const draggedKeyRef = useRef<CalendarFragmentKey | null>(null);
  const [draggedKey, setDraggedKey] = useState<CalendarFragmentKey | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  const startDrag = useCallback((event: React.DragEvent, key: CalendarFragmentKey) => {
    draggedKeyRef.current = key;
    setDraggedKey(key);
    event.dataTransfer.effectAllowed = 'move';
    // Some browsers cancel the drag outright unless some payload is set.
    event.dataTransfer.setData('text/plain', key);
  }, []);

  const endDrag = useCallback(() => {
    draggedKeyRef.current = null;
    setDraggedKey(null);
    setDropTarget(null);
  }, []);

  /** Called by a placed item: drops land before or after it depending on which half was hovered. */
  const hoverItem = useCallback((event: React.DragEvent, zone: LayoutZone, index: number, horizontal: boolean) => {
    if (!draggedKeyRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const past = horizontal ? event.clientX > rect.left + rect.width / 2 : event.clientY > rect.top + rect.height / 2;
    setDropTarget({ zone, index: past ? index + 1 : index });
  }, []);

  /**
   * Called by a zone container and by its tail strip: appends to the end.
   *
   * Items stop propagation on their own `dragover`, so this only fires when the pointer is over the
   * zone's own space and not over a fragment — which means it can always claim the append position.
   * (It used to bail out whenever the target was already in this zone, which made the last slot
   * nearly unreachable: once any item had claimed the target, moving into the tail never updated it.)
   */
  const hoverZone = useCallback((event: React.DragEvent, zone: LayoutZone, count: number) => {
    if (!draggedKeyRef.current) return;
    event.preventDefault();
    setDropTarget(current => (current?.zone === zone && current.index === count ? current : { zone, index: count }));
  }, []);

  const commitDrop = useCallback(
    (event: React.DragEvent, fallbackZone: LayoutZone, onDrop: (key: CalendarFragmentKey, target: DropTarget) => void) => {
      event.preventDefault();
      event.stopPropagation();
      const key = draggedKeyRef.current ?? (event.dataTransfer.getData('text/plain') as CalendarFragmentKey);
      if (key) onDrop(key, dropTarget?.zone === fallbackZone ? dropTarget : { zone: fallbackZone, index: -1 });
      endDrag();
    },
    [dropTarget, endDrag],
  );

  return { draggedKey, dropTarget, startDrag, endDrag, hoverItem, hoverZone, commitDrop };
};
